/**
 * PUBLIC WEBSITE COPY — meridiananesthesia.com.
 *
 * Every word the marketing pages print lives here (plus the leadership bios in
 * leadership.ts), so one test can hold all of it to the rules:
 * __tests__/website-content-policy.test.ts.
 *
 * Built from the vetted candidate-portal content (app/lib/portal/*-content.ts)
 * and held to the same fence, with two additions for a public, indexed page:
 *  - Never "physician-led", "led by physicians", or supervision / oversight /
 *    direction framing. CRNAs read this site (Mark, 2026-09-26).
 *  - No dollar figures, no rates, no partnership economics, no competitor or
 *    agency names, no payer or deal material.
 *  - Name the hospitals; never count them. A hospital before go-live appears only
 *    when Mark has approved announcing it, always with its absolute go-live date
 *    (lib/hospitals.ts ANNOUNCED_UPCOMING).
 *  - Hospitals read this site: nothing that ranks, compares, or comments on how
 *    a hospital runs. Mark appears only in the leadership roster ("our
 *    President" elsewhere), and never as founder or CEO in a title.
 *  - Headings carry facts, not slogans; body text is plain and specific.
 *  - NPH is our parent organization: "a Division of National Partners in
 *    Healthcare" (DIVISION_LINE, capital D), never "management partner". No
 *    link out to nphllc.com, whose homepage leads with "Physician Led".
 */

import type { ApplyChoice, ApplyPosition, ApplyRole } from './apply'
import { SITE } from './site'

export interface Fact {
  label: string
  value: string
}

export interface Pillar {
  icon: PillarIcon
  title: string
  body: string
}

export type PillarIcon =
  | 'stethoscope'
  | 'activity'
  | 'graduation'
  | 'calendar'
  | 'briefcase'
  | 'shield'
  | 'users'
  | 'heart'
  | 'star'
  | 'building'
  | 'clock'
  | 'baby'
  | 'bone'
  | 'syringe'
  | 'monitor'
  | 'handshake'
  | 'microscope'

/** The site's headline — Mark's wording, 2026-09-26. */
export const HEADLINE = 'DFW’s Premier Anesthesia Practice'

/**
 * How the site names our relationship to NPH — Mark, 2026-09-26: "Meridian
 * Anesthesia, a Division of National Partners in Healthcare", capital D, with
 * NPH called our parent organization. Never "management partner". The public
 * site is the one on-screen place this line appears; the platform, the portals,
 * and login stay without it.
 */
export const DIVISION_LINE = 'A Division of National Partners in Healthcare'


// ── Home ─────────────────────────────────────────────────────────────────────

export const HOME = {
  meta: {
    title: 'Meridian Anesthesia | DFW’s Premier Anesthesia Practice',
    description:
      'Meridian Anesthesia, a Division of National Partners in Healthcare, is an anesthesia group of physicians and CRNAs at Medical City Arlington, Las Colinas, and Plano, and at Methodist Richardson Medical Center from December 1, 2026.',
  },
  hero: {
    eyebrow: 'Meridian Anesthesia · Dallas–Fort Worth',
    title: HEADLINE,
    lede: 'Meridian Anesthesia, a Division of National Partners in Healthcare, is an anesthesia group of physicians and CRNAs. We serve Medical City Arlington, Las Colinas, and Plano — and, beginning December 1, 2026, Methodist Richardson Medical Center.',
    primary: { label: 'Our services', href: 'services' as const },
    secondary: { label: 'Careers', href: 'careers' as const },
  },
  glance: {
    eyebrow: 'The practice',
    title: 'One anesthesia team, from a Level I trauma center to a community hospital',
    paragraphs: [
      'We provide anesthesia for surgery, obstetrics, cardiac, and trauma at multiple hospitals across the Metroplex. Plano is a Level I trauma center, Arlington a Level II, and Las Colinas a community hospital with a steadier pace.',
      'Anesthesiologists and CRNAs practice as one team: everybody does the whole process, from the pre-op to the block to the sign-out. Obstetric anesthesia is in house around the clock at every hospital, and cardiac cases are covered by fellowship-trained, TEE-certified cardiac anesthesiologists.',
    ],
    facts: [
      { label: 'Hospitals', value: 'Arlington, Las Colinas, and Plano; Methodist Richardson from December 1, 2026' },
      { label: 'Trauma', value: 'Level I at Plano, Level II at Arlington' },
      { label: 'Obstetrics', value: 'In house around the clock' },
      { label: 'Cardiac', value: 'Fellowship-trained, TEE-certified anesthesiologists' },
      { label: 'Clinicians', value: 'Anesthesiologists and CRNAs' },
      { label: 'Parent organization', value: 'National Partners in Healthcare' },
    ] satisfies Fact[],
  },
  hospitals: {
    eyebrow: 'Our hospitals',
    title: 'Arlington, Las Colinas, Plano, and Richardson',
    body: 'The case mix and the pace differ from one hospital to the next, and Methodist Richardson Medical Center joins the practice on December 1, 2026. Our clinicians work from one primary hospital, with the option to credential at a second.',
  },
  audiences: [
    {
      eyebrow: 'For hospitals',
      title: 'Anesthesia coverage built around your program',
      body: 'Surgery, obstetrics, cardiac, and trauma, staffed to your schedule by anesthesiologists and CRNAs working as one team, with the backing of National Partners in Healthcare, our parent organization.',
      cta: { label: 'Our services', href: 'services' as const },
    },
    {
      eyebrow: 'For CRNAs and anesthesiologists',
      title: 'Full-scope practice, paid by the shift',
      body: 'Blocks, lines, and epidurals are yours to do. The scheduled shift is guaranteed, Meridian covers malpractice and credentialing, and you can work full-time or PRN.',
      cta: { label: 'Careers', href: 'careers' as const },
    },
  ],
  leadership: {
    eyebrow: 'Leadership',
    title: 'Clinicians who practice at our hospitals',
    body: 'Our officers and clinical leaders take care of patients in the same operating rooms as the rest of the group.',
    cta: 'Meet our leadership',
  },
  closing: {
    eyebrow: 'Contact',
    title: 'Talk with Meridian',
    body: 'Hospital leaders, clinicians, and partners: send us a note or call the practice line, and a member of our team will get back to you.',
    cta: 'Contact us',
  },
}

// ── About ────────────────────────────────────────────────────────────────────

export const ABOUT = {
  meta: {
    title: 'About Meridian',
    description:
      'Meridian Anesthesia, a Division of National Partners in Healthcare, is an anesthesia group of physicians and CRNAs at multiple hospitals in Dallas–Fort Worth, with clinical decisions made locally.',
  },
  hero: {
    eyebrow: 'About Meridian',
    title: 'An anesthesia group of physicians and CRNAs',
    lede: 'We practice at multiple large hospitals across Dallas–Fort Worth as a Division of National Partners in Healthcare (NPH). Clinical decisions are made here, by the people doing the work, and NPH runs the business side.',
  },
  principles: {
    eyebrow: 'How we practice',
    title: 'What you can expect from us',
    items: [
      {
        icon: 'users',
        title: 'One team',
        body: 'Everybody does the whole process here. Pre-ops, blocks, lines, and sign-outs are not reserved for one discipline over the other. It is all about workflow.',
      },
      {
        icon: 'graduation',
        title: 'Skills are shared',
        body: 'If you are good at something, the expectation is that you teach it to everyone else, because the whole team is better for it.',
      },
      {
        icon: 'calendar',
        title: 'Honest scheduling',
        body: 'We staff with a little more coverage than we strictly need. Assigned shifts are not canceled, and if the cases cancel, the shift still pays.',
      },
      {
        icon: 'star',
        title: 'Decisions made locally',
        body: 'Clinical decisions are made by the clinicians doing the work. NPH, our parent organization, handles billing, contracting, and administration.',
      },
    ] satisfies Pillar[],
  },
  facts: {
    eyebrow: 'At a glance',
    title: 'The practice today',
    items: [
      { label: 'Hospitals', value: 'Medical City Arlington, Medical City Las Colinas, and Medical City Plano; Methodist Richardson Medical Center from December 1, 2026' },
      { label: 'Trauma', value: 'Level I at Plano, Level II at Arlington' },
      { label: 'Case mix', value: 'Trauma, cardiac, neuro and spine, vascular, hepatobiliary, orthopedics, and labor and delivery' },
      { label: 'Obstetrics', value: 'In house around the clock at every hospital' },
      { label: 'Charting', value: 'Graphium, on an iPad' },
      { label: 'Parent organization', value: 'National Partners in Healthcare' },
    ] satisfies Fact[],
  },
  partnership: {
    eyebrow: 'Our parent organization',
    title: 'A Division of National Partners in Healthcare',
    // NPH facts as nphllc.com published them on 2026-09-26, kept to what does
    // not go stale: anesthesia-focused, national, headquartered in Richardson
    // (the market and state counts wait for NPH to confirm them). What NPH
    // brings is Mark's 8/1 list, unchanged.
    intro:
      'Meridian Anesthesia is a Division of National Partners in Healthcare (NPH), a national anesthesia organization with practices in markets across the United States. Our practice office is at NPH’s headquarters in Richardson, Texas.',
    columns: [
      {
        title: 'What NPH brings',
        items: ['Financial backing', 'Billing and collections', 'Credentialing support', 'Contracting'],
      },
      {
        title: 'What stays local',
        items: [
          'Clinical decisions, made by the clinicians doing the work',
          'Clinical leaders at each of our hospitals',
          'Scheduling and staffing',
          'Hiring decisions',
          'The operations platform we built for this practice',
        ],
      },
    ],
    closing:
      'Being part of NPH does not mean being run from a corporate office. NPH carries the business side, and Meridian keeps local governance and clinical autonomy: we run our own group. The result is the backing of a national anesthesia organization, with clinical decisions made here.',
  },
  closing: {
    eyebrow: 'Leadership',
    title: 'Meet the people who lead Meridian',
    body: 'Our officers and clinical leaders practice at our hospitals.',
    cta: 'Our leadership',
  },
}

// ── Services (hospital-facing) ────────────────────────────────────────────────

export const SERVICES = {
  meta: {
    title: 'Anesthesia Services',
    description:
      'Hospital anesthesia coverage across Dallas–Fort Worth: surgery, obstetrics, cardiac, trauma, and regional anesthesia, provided by anesthesiologists and CRNAs working as one team.',
  },
  hero: {
    eyebrow: 'For hospitals',
    title: 'Anesthesia for surgery, obstetrics, cardiac, and trauma',
    lede: 'We provide the anesthesia department for hospitals across Dallas–Fort Worth, from a Level I trauma center to a community hospital, staffed to each hospital’s schedule and service lines.',
    cta: 'Talk with us about coverage',
  },
  models: {
    eyebrow: 'Coverage',
    title: 'Two ways to staff a department',
    body: 'Most of our hospitals use a care team. Some service lines are covered by anesthesiologists alone. We build the model around the hospital’s program, not the other way around.',
    items: [
      {
        icon: 'users',
        title: 'Anesthesia care team',
        body: 'Anesthesiologists and CRNAs working together, staffed to your case volume, service lines, and hours. CRNAs do their own blocks, lines, and epidurals, and an anesthesiologist is always available to the room.',
      },
      {
        icon: 'stethoscope',
        title: 'Anesthesiologist coverage',
        body: 'Anesthesiologists providing the anesthetic themselves, for the programs and service lines that call for it. Cardiac, for example, runs with the anesthesiologist in the room.',
      },
    ] satisfies Pillar[],
  },
  lines: {
    eyebrow: 'Clinical services',
    title: 'What we cover',
    items: [
      { icon: 'activity', title: 'Trauma', body: 'Level I and Level II trauma, with anesthesia in house around the clock.' },
      { icon: 'heart', title: 'Cardiac and vascular', body: 'Fellowship-trained, TEE-certified cardiac anesthesiologists, with vascular and thoracic surgery.' },
      { icon: 'baby', title: 'Obstetrics', body: 'Labor epidurals and cesarean sections, with obstetric anesthesia in house around the clock.' },
      { icon: 'bone', title: 'Orthopedics and spine', body: 'Joints, fractures, orthopedic trauma, and major spine surgery.' },
      { icon: 'syringe', title: 'Regional anesthesia and acute pain', body: 'Ultrasound-guided nerve blocks for surgical pain, a large part of the day with our orthopedic and spine volume.' },
      { icon: 'microscope', title: 'Complex and robotic surgery', body: 'Hepatobiliary, esophageal, neurosurgical, robotic, and gynecologic surgery.' },
    ] satisfies Pillar[],
  },
  approach: {
    eyebrow: 'How we work',
    title: 'What a hospital gets from Meridian',
    items: [
      {
        icon: 'calendar',
        title: 'Coverage built to your schedule',
        body: 'We staff to the OR schedule, the add-ons, and the call, and we carry a little more coverage than strictly needed so rooms keep running.',
      },
      {
        icon: 'star',
        title: 'Clinical leadership on site',
        body: 'Our clinical leaders practice at our hospitals. Practice decisions are made here, by the clinicians doing the work.',
      },
      {
        icon: 'monitor',
        title: 'Our own operations platform',
        body: 'Scheduling, credentialing, time records, and case records run on software we built for this practice, so questions get fast, accurate answers.',
      },
      {
        icon: 'handshake',
        title: 'A Division of National Partners in Healthcare',
        body: 'NPH, our parent organization, is a national anesthesia organization. It provides the financial backing, billing and collections, credentialing support, and contracting behind the practice, and clinical decisions stay here.',
      },
    ] satisfies Pillar[],
  },
  closing: {
    eyebrow: 'Coverage',
    title: 'Talk with us about your anesthesia program',
    body: 'Tell us about your hospital, your case mix, and what you need from an anesthesia group, and we will follow up directly.',
    cta: 'Contact us',
  },
}

// ── Our Team ─────────────────────────────────────────────────────────────────
// Leadership on top, then every provider under hospital tabs (Mark, 2026-09-27).

export const TEAM_PAGE = {
  meta: {
    title: 'Our Team',
    description:
      'The leadership, anesthesiologists, and CRNAs of Meridian Anesthesia, a Division of National Partners in Healthcare, and the NPH executives behind the practice.',
  },
  hero: {
    eyebrow: 'Our Team',
    title: 'The people of Meridian',
    lede: 'Our leaders, anesthesiologists, and CRNAs practice at our hospitals. Meridian Anesthesia is a Division of National Partners in Healthcare.',
  },
  leadership: {
    eyebrow: 'Leadership',
    title: 'The people who lead Meridian',
  },
  practice: {
    eyebrow: 'Practice management',
    title: 'The team behind the practice',
  },
  providers: {
    eyebrow: 'Our providers',
    title: 'Anesthesiologists and CRNAs',
    tabsLabel: 'Providers by hospital',
    // Mark's tabs, in his order. A hospital before go-live says when it begins.
    tabs: [
      { key: 'all', label: 'All Providers' },
      { key: 'arlington', label: 'Medical City Arlington' },
      { key: 'plano', label: 'Medical City Plano' },
      { key: 'las-colinas', label: 'Medical City Las Colinas' },
      {
        key: 'methodist-richardson',
        label: 'Methodist Richardson',
        empty: 'Meridian’s coverage at Methodist Richardson Medical Center begins on December 1, 2026.',
      },
    ] as { key: string; label: string; empty?: string }[],
    empty: 'Our providers at this hospital will be listed here.',
  },
  nph: {
    eyebrow: 'Our parent organization',
    title: 'National Partners in Healthcare',
    body: 'Meridian is a Division of NPH. NPH handles the business side — financial backing, billing and collections, credentialing support, and contracting — and Meridian keeps local governance and clinical autonomy.',
  },
  closing: {
    eyebrow: 'Careers',
    title: 'Practice with this team',
    body: 'We hire CRNAs and anesthesiologists, full-time and PRN, across our hospitals.',
    cta: 'Careers at Meridian',
  },
}

// ── Careers ──────────────────────────────────────────────────────────────────

export const CAREERS = {
  meta: {
    title: 'Careers — CRNA and Anesthesiologist Positions in Dallas–Fort Worth',
    description:
      'CRNA and anesthesiologist positions, full-time and PRN, at Medical City Arlington, Las Colinas, and Plano, and at Methodist Richardson Medical Center from December 1, 2026. Paid by the shift, with malpractice and credentialing covered.',
  },
  hero: {
    eyebrow: 'Careers',
    title: 'Practice with Meridian',
    lede: 'CRNA and anesthesiologist positions, full-time and PRN, at multiple large hospitals across Dallas–Fort Worth. Meridian is a Division of National Partners in Healthcare, so you practice in a local group with its own clinical leaders and a national anesthesia organization behind it.',
    primary: 'Apply now',
    secondary: 'Open positions',
  },
  facts: {
    eyebrow: 'The basics',
    title: 'How work here is set up',
    items: [
      { label: 'Hospitals', value: 'Arlington, Las Colinas, and Plano; Methodist Richardson from December 1, 2026' },
      { label: 'Pay', value: 'By the shift; if the cases cancel, the shift still pays' },
      { label: 'Malpractice', value: 'Provided and paid by Meridian, full-time and PRN' },
      { label: 'Credentialing', value: 'Paid by Meridian, including a second hospital' },
      { label: 'Schedule', value: '8, 10, and 12-hour days, full-time or PRN' },
      { label: 'Charting', value: 'Graphium on an iPad, about five minutes a chart' },
    ] satisfies Fact[],
  },
  crna: {
    eyebrow: 'For CRNAs',
    title: 'Full-scope practice across multiple hospitals in DFW',
    // CRNA_CONTENT pillars, portal role-content.ts (vetted wording).
    pillars: [
      {
        icon: 'stethoscope',
        title: 'You practice to full scope',
        body: 'Our CRNAs do their own blocks, arterial and central lines, and epidurals, and take their patients from pre-op to sign-out. We want providers who can run the whole case.',
      },
      {
        icon: 'activity',
        title: 'Hard cases, with help when you want it',
        body: 'Level I and Level II trauma, hepatobiliary and esophageal surgery, major spine and vascular. An anesthesiologist is there when you need one, and you are trusted to run the case.',
      },
      {
        icon: 'graduation',
        title: 'Skills get taught, not guarded',
        body: 'If you are good at something, you teach it. Our anesthesiologists teach blocks and lines, and the initials behind your name do not decide whether you get good at something. Experience does.',
      },
      {
        icon: 'calendar',
        title: 'The schedule is modular',
        body: '8, 10, and 12-hour days, with longer OB and trauma shifts at Plano for the people who want them. Tell us the week you want and we will tell you honestly whether we can match it.',
      },
      {
        icon: 'briefcase',
        title: 'Every scheduled shift pays',
        body: 'If the cases cancel or you are released early, the shift pays in full. If you stay late, the extra time is paid. Every shift is itemized in the provider app.',
      },
      {
        icon: 'shield',
        title: 'Malpractice and credentialing are on us',
        body: 'Meridian provides your malpractice coverage and pays your credentialing and privileging fees — full-time and PRN.',
      },
    ] satisfies Pillar[],
  },
  md: {
    eyebrow: 'For anesthesiologists',
    title: 'A partnership track, with the clinical practice to keep you sharp',
    pillars: [
      {
        icon: 'users',
        title: 'A two-year partnership track',
        body: 'Full-time physicians join on a two-year partnership track. Its terms are a schedule on the agreement you sign when you join, so there is nothing new to negotiate at the two-year mark.',
      },
      {
        icon: 'stethoscope',
        title: 'You still do your own cases',
        body: 'Roughly thirty to forty percent of the work is solo, by design, so your own hands stay in the cases.',
      },
      {
        icon: 'briefcase',
        title: 'Income from month one',
        body: 'Shift-based compensation means no ramp-up period and no waiting on collections to build. Your first full month looks like a normal month.',
      },
      {
        icon: 'calendar',
        title: 'Call built around downtime',
        body: 'Arlington’s trauma nights have their own physician shift. Call is paid as a shift, and post-call days are off.',
      },
      {
        icon: 'heart',
        title: 'Cardiac and OB have their own teams',
        body: 'Cardiac is covered by fellowship-trained, TEE-certified cardiac anesthesiologists, and obstetrics is covered in house around the clock at every hospital.',
      },
      {
        icon: 'star',
        title: 'Local clinical governance',
        body: 'Practice decisions are made by the clinicians doing the work. National Partners in Healthcare, our parent organization, handles billing, contracting, and administration.',
      },
    ] satisfies Pillar[],
  },
  positions: {
    eyebrow: 'Open positions',
    title: 'Full-time and PRN positions',
    body: 'Apply once. We will talk with you about which hospital and schedule fit what you want. Credentialing for Methodist Richardson, which opens December 1, 2026, has already started.',
    items: [
      {
        title: 'CRNA, full-time',
        apply: { role: 'CRNA', position: 'FULL_TIME' } satisfies ApplyChoice,
        detail: 'About 1,840 hours a year, and it flexes: some of our CRNAs work 30 hours a week and some work 60. One primary hospital, credentialed at a second at no cost to you.',
      },
      {
        title: 'CRNA, PRN',
        apply: { role: 'CRNA', position: 'PRN' } satisfies ApplyChoice,
        detail: 'No minimums. Put your availability in the app as far ahead as you like; PRN availability goes on the schedule first, and a confirmed shift is not canceled.',
      },
      {
        title: 'Anesthesiologist, full-time',
        apply: { role: 'MD', position: 'FULL_TIME' } satisfies ApplyChoice,
        detail: 'A two-year partnership track, paid by the shift from the first month, with a primary hospital and privileges at a second.',
      },
      {
        title: 'Anesthesiologist, PRN',
        apply: { role: 'MD', position: 'PRN' } satisfies ApplyChoice,
        detail: 'Shift-based work at our hospitals, scheduled around the availability you give us.',
      },
    ],
  },
  closing: {
    eyebrow: 'Next',
    title: 'Talk with our President',
    // SHOWCASE_NEXT body, portal showcase-content.ts (Mark, 2026-09-25).
    body: 'You will not be handed off to a recruiter. You will talk directly with our President about the opportunity. Come with your questions and with the week you would like to work.',
    primary: 'Apply now',
    secondary: 'Contact recruiting',
  },
}

// ── Contact ──────────────────────────────────────────────────────────────────

// ── Apply ────────────────────────────────────────────────────────────────────
// The first-party application form (components/ApplyForm.tsx, route
// app/api/website/apply). No licensure question on purpose: Meridian
// credentialing handles licensure after the first conversation.

export const APPLY = {
  meta: {
    title: 'Apply — CRNA and Anesthesiologist Positions',
    description:
      'Apply for a full-time or PRN CRNA or anesthesiologist position with Meridian Anesthesia in Dallas–Fort Worth. You do not need a CV to apply.',
  },
  hero: {
    eyebrow: 'Apply',
    title: 'Apply to Meridian',
    lede: 'Tell us who you are and the work you want. A member of our team will get back to you. You do not need a CV to apply.',
  },
  form: {
    title: 'Your application',
    labels: {
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      phone: 'Phone',
      role: 'I am a',
      position: 'Position',
      employer: 'Current employer',
      currentPosition: 'Current position',
      availableFrom: 'Earliest availability',
      graduationDate: 'Expected graduation date',
      spanish: 'I speak Spanish',
      notes: 'Anything else we should know?',
    },
    hints: {
      notes: 'The hospitals, weeks, or kinds of cases you want, or anything else you would like us to know.',
    },
    select: 'Select',
    roles: { CRNA: 'CRNA', SRNA: 'SRNA (student)', MD: 'Anesthesiologist' } satisfies Record<ApplyRole, string>,
    positions: { FULL_TIME: 'Full-time', PRN: 'PRN' } satisfies Record<ApplyPosition, string>,
    submit: 'Submit application',
    sending: 'Sending…',
    sent: {
      title: 'Application received',
      body: 'Thank you for applying. A member of our team will get back to you shortly.',
      again: 'Submit another application',
    },
    error: 'Something went wrong. Please try again, or call or email us directly.',
  },
  aside: {
    lead: 'Prefer to talk first?',
    contact: 'Send us a message',
  },
}

export const CONTACT = {
  meta: {
    title: 'Contact',
    description: `Contact Meridian Anesthesia about anesthesia coverage, careers, or partnership. Practice line ${SITE.phoneDisplay}.`,
  },
  hero: {
    eyebrow: 'Contact',
    title: 'Talk with Meridian',
    lede: 'Hospital leaders, clinicians, and partners: send us a note, and a member of our team will get back to you.',
  },
  form: {
    title: 'Send a message',
    sent: {
      title: 'Message received',
      body: 'Thank you for reaching out. A member of our team will get back to you shortly.',
      again: 'Send another message',
    },
    error: 'Something went wrong. Please try again, or email us directly.',
  },
  rail: {
    clinicians: {
      title: 'Clinicians',
      body: 'Looking for a CRNA or anesthesiologist position? Start on our careers page.',
      cta: 'Careers',
    },
    providers: {
      title: 'Meridian providers',
      body: 'Schedules, time records, and documents are in the provider platform.',
      cta: 'Provider log in',
    },
  },
}

// ── Footer ───────────────────────────────────────────────────────────────────

export const FOOTER = {
  blurb: 'An anesthesia group of physicians and CRNAs at Medical City Arlington, Las Colinas, and Plano, and at Methodist Richardson Medical Center from December 1, 2026.',
  division: `Meridian Anesthesia · ${DIVISION_LINE}`,
}
